import { infiniteQueryOptions, queryOptions, useInfiniteQuery } from "@tanstack/react-query";
import api from "~/shared/api/core/api";
import { Partner, PartnerOpps } from "~/shared/api/partners/partners.types";
import { Params } from "~/shared/api/types/params";
import { createUseInfiniteFilter } from "~/shared/api/utils/filters";
import { client } from "~/shared/lib/client";
import { PaginatedResponse } from "~/shared/types/pagination.types";
import { addFullNameToDataItems } from "~/shared/util/addFullNameToDataItems";
import { ensureApiData } from "~/shared/util/api.helpers";
import { createInfinitePaginationConfig } from "~/shared/util/createInfinitePaginationConfig";
import { trpcClient } from "../core/trpc";

export const PARTNERS_KEYS = {
  all: ["partners"] as const,
  detail: (id: string) => [...PARTNERS_KEYS.all, id] as const,
  list: (params: Params) => [...PARTNERS_KEYS.all, "list", params] as const,
  opportunities: (type: PartnerOpps) => [...PARTNERS_KEYS.all, "opps", type] as const,
  opportunitiesList: (type: PartnerOpps, params: Params) => {
    return [...PARTNERS_KEYS.opportunities(type), "list", params] as const;
  },
  types: (params: Params) => [...PARTNERS_KEYS.all, "types", params] as const,
  spaces: (params: Params) => [...PARTNERS_KEYS.all, "spaces", params] as const,
  stages: (params: Params) => [...PARTNERS_KEYS.all, "stages", params] as const,
  recruiters: (partnerId: string | number | null, params: Params = {}) => {
    return [...PARTNERS_KEYS.all, "recruiters", partnerId, params] as const;
  },
  jobQuestionDefinitions: (partnerId: number, status: "active" | "archived") => {
    return [...PARTNERS_KEYS.detail(partnerId.toString()), "jobQuestionDefinitions", status] as const;
  },
  listInterviewRoundTypes: (partnerId: number, status: "active" | "archived") => {
    return [...PARTNERS_KEYS.detail(partnerId.toString()), "interviewRoundTypes", status] as const;
  },
  recruiterDetail: (partnerId: number, contactId: number) => {
    return [...PARTNERS_KEYS.detail(partnerId.toString()), "recruiter", contactId.toString()] as const;
  },
};

export interface PartnerListItem {
  id: number;
  name: string;
  logo?: {
    location: string;
  } | null;
  hprCount: number | null;
  hprNoInterviewCount: number | null;
}

export const PARTNERS_QUERIES = {
  detail: (id: string) => {
    return queryOptions({
      queryKey: PARTNERS_KEYS.detail(id),
      queryFn: async ({ signal }) => {
        const response = await api.get<Partner>(`/partners/${id}`, {
          signal,
        });

        return response.data;
      },
    });
  },
  partnersList: (params: Params) => {
    return queryOptions({
      queryKey: PARTNERS_KEYS.list(params),
      queryFn: async ({ signal }) => {
        const response = await api.get<PaginatedResponse<PartnerListItem>>("/partners", {
          ...params,
          signal,
        });

        return response.data;
      },
    });
  },
  opportunities: (type: PartnerOpps, params = {}) => {
    return queryOptions({
      queryKey: PARTNERS_KEYS.opportunitiesList(type, params),
      queryFn: async ({ signal }) => {
        const response = await api.get(`/partners/opportunities/${type}`, {
          ...params,
          signal,
        });

        return response.data;
      },
    });
  },
  infiniteTypes: (params: InfiniteTypeQueryParams) => {
    const paginationConfig = createInfinitePaginationConfig(params, 20);

    return infiniteQueryOptions({
      queryKey: PARTNERS_KEYS.types({
        params: paginationConfig.keyParams,
      }),
      queryFn: async ({ pageParam = 1, signal }) => {
        const { skip, limit, term } = paginationConfig.queryFnParams(pageParam);
        const result = await trpcClient.hq.companies.types.list.query(
          {
            skip,
            limit,
            term: term as string | undefined,
          },
          {
            signal,
          },
        );

        return { items: result.data, total: result.total };
      },
      ...paginationConfig.opts,
    });
  },
  infiniteSpaces: (params: { [key: string]: unknown }) => {
    const paginationConfig = createInfinitePaginationConfig(params, 20);

    return infiniteQueryOptions({
      queryKey: PARTNERS_KEYS.spaces({
        params: paginationConfig.keyParams,
      }),
      queryFn: async ({ pageParam = 1, signal }) => {
        const response = await api.get<PaginatedResponse<{ id: number; name: string }>>("/partners/spaces", {
          signal,
          params: paginationConfig.queryFnParams(pageParam),
        });

        return response.data;
      },
      ...paginationConfig.opts,
    });
  },
  infiniteStages: (params: { [key: string]: unknown }) => {
    const paginationConfig = createInfinitePaginationConfig(params, 20);

    return infiniteQueryOptions({
      queryKey: PARTNERS_KEYS.stages({
        params: paginationConfig.keyParams,
      }),
      queryFn: async ({ pageParam = 1, signal }) => {
        const response = await api.get<PaginatedResponse<{ id: number; name: string }>>("/partners/stages", {
          signal,
          params: paginationConfig.queryFnParams(pageParam),
        });

        return response.data;
      },
      ...paginationConfig.opts,
    });
  },
  infinitePartnersList: (params: { [key: string]: unknown }) => {
    const paginationConfig = createInfinitePaginationConfig(
      {
        orderBy: "name",
        orderDir: "ASC",
        ...params,
      },
      20,
    );

    return infiniteQueryOptions({
      queryKey: PARTNERS_KEYS.list({
        params: paginationConfig.keyParams,
      }),
      queryFn: async ({ pageParam = 1, signal }) => {
        const response = await api.get<PaginatedResponse<unknown>>("/partners", {
          params: paginationConfig.queryFnParams(pageParam),
          signal,
        });

        return response.data;
      },
      ...paginationConfig.opts,
    });
  },
  infiniteRecruitersList: (partnerId: number | null) => {
    return (params: { [key: string]: unknown }) => {
      const paginationConfig = createInfinitePaginationConfig(
        {
          orderBy: "name",
          orderDir: "ASC",
          ...params,
        },
        20,
      );

      return infiniteQueryOptions({
        queryKey: PARTNERS_KEYS.recruiters(partnerId, {
          params: paginationConfig.keyParams,
        }),
        queryFn: async ({ pageParam = 1, signal }) => {
          const response = await api.get<
            PaginatedResponse<{
              id: number;
              firstName: string;
              lastName: string;
              email: string;
              headshot?: {
                location: string;
              };
            }>
          >(`/partners/${partnerId}/recruiters`, {
            params: paginationConfig.queryFnParams(pageParam),
            signal,
          });

          return response.data;
        },
        select: addFullNameToDataItems,
        ...paginationConfig.opts,
      });
    };
  },
  jobQuestionDefinitions: (partnerId: number, status: "active" | "archived" = "active") => {
    return queryOptions({
      queryKey: PARTNERS_KEYS.jobQuestionDefinitions(partnerId, status),
      queryFn: async () => {
        const { data } = await client.GET("/partners/{partnerId}/job-question-definitions", {
          params: {
            path: {
              partnerId,
            },
            query: {
              status,
            },
          },
        });

        return ensureApiData(data, "GET /partners/{partnerId}/job-question-definitions");
      },
    });
  },
  listInterviewRoundTypes: (partnerId: number, status: "active" | "archived" = "active") => {
    return queryOptions({
      queryKey: PARTNERS_KEYS.listInterviewRoundTypes(partnerId, status),
      queryFn: async () => {
        const { data } = await client.GET("/partners/{partnerId}/interview-round-types", {
          params: {
            path: {
              partnerId,
            },
            query: {
              published: status === "active",
            },
          },
        });

        return ensureApiData(data, "GET /partners/{partnerId}/interview-round-types");
      },
    });
  },
  recruiterDetail: (partnerId: number, contactId: number) => {
    return queryOptions({
      queryKey: PARTNERS_KEYS.recruiterDetail(partnerId, contactId),
      queryFn: async () => {
        const response = await api.get(`/partners/${partnerId}/recruiters/${contactId}`);
        return response.data;
      },
    });
  },
};

type InfiniteTypeQueryParams = Parameters<typeof trpcClient.hq.companies.types.list.query>[0];

export function useInfinitePartnerFilter(params: Record<string, unknown>) {
  return createUseInfiniteFilter("partners")({
    ...params,
    orderDir: "ASC",
    orderBy: "name",
  });
}

export function useInfinitePartnerTypeFilter(params: InfiniteTypeQueryParams) {
  return useInfiniteQuery(PARTNERS_QUERIES.infiniteTypes(params));
}
